import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { 
  Clock, Check, X, AlertCircle, RefreshCw, 
  MessageCircle, Phone, ArrowRight, Calendar
} from "lucide-react";
import Layout from "../components/Layout";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SubscriptionStatus() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchPaymentMethods();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/subscriptions/status`);
      setStatus(response.data);
    } catch (error) {
      toast.error("Erro ao carregar status");
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/api/payment-methods`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error("Error fetching payment methods", error);
    }
  };

  const getStatusConfig = (requestStatus) => {
    const configs = {
      awaiting_proof: {
        icon: <Clock className="text-yellow-600" size={24} />,
        label: "Aguardando comprovante",
        color: "text-yellow-600",
        bg: "bg-yellow-50",
        description: "Realize o pagamento e envie o comprovante."
      },
      proof_submitted: {
        icon: <Clock className="text-blue-600" size={24} />,
        label: "Em análise",
        color: "text-blue-600",
        bg: "bg-blue-50",
        description: "Seu comprovante foi recebido e está sendo analisado."
      },
      approved: {
        icon: <Check className="text-green-600" size={24} />,
        label: "Aprovado",
        color: "text-green-600",
        bg: "bg-green-50",
        description: "Pagamento confirmado! Seu acesso está ativo."
      },
      rejected: {
        icon: <X className="text-red-600" size={24} />,
        label: "Rejeitado",
        color: "text-red-600",
        bg: "bg-red-50",
        description: "Sua solicitação foi rejeitada."
      },
      correction_needed: {
        icon: <AlertCircle className="text-orange-600" size={24} />,
        label: "Correção necessária",
        color: "text-orange-600",
        bg: "bg-orange-50",
        description: "Por favor, envie um novo comprovante."
      }
    };
    return configs[requestStatus] || configs.awaiting_proof;
  };

  const getPlanLabel = (planId) => {
    switch(planId) {
      case "base": return "Base";
      case "advanced": return "Avançado";
      case "complete": return "Completo";
      default: return planId;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="loading-skeleton w-12 h-12 rounded-full"></div>
        </div>
      </Layout>
    );
  }

  const request = status?.latest_request;
  const statusConfig = request ? getStatusConfig(request.status) : null;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="subscription-status-page">
        <div className="mb-8">
          <h1 className="font-serif text-2xl md:text-3xl mb-2">Minha Assinatura</h1>
          <p className="text-muted-foreground">
            Acompanhe o status da sua assinatura e renove quando necessário.
          </p>
        </div>

        {/* Current Access Status */}
        <Card className="p-6 mb-6">
          <h3 className="font-mono text-xs uppercase tracking-widest mb-4">Status do Acesso</h3>
          
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Plano atual</p>
              <p className="font-medium text-lg">
                {status?.subscription_tier ? getPlanLabel(status.subscription_tier) : "Nenhum"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Dias restantes</p>
              <p className={`font-serif text-2xl ${
                status?.days_remaining <= 7 
                  ? status?.days_remaining <= 3 
                    ? "text-red-600" 
                    : "text-yellow-600"
                  : "text-green-600"
              }`}>
                {status?.days_remaining ?? "-"}
              </p>
            </div>
          </div>

          {status?.access_expires_at && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar size={14} />
              <span>Expira em {formatDate(status.access_expires_at)}</span>
            </div>
          )}

          {/* Expiration Warnings */}
          {status?.days_remaining !== null && status?.days_remaining <= 7 && (
            <div className={`mt-4 p-3 rounded-none ${
              status.days_remaining <= 3 ? "bg-red-50 text-red-800" : "bg-yellow-50 text-yellow-800"
            }`}>
              <p className="text-sm font-medium">
                {status.days_remaining === 0 
                  ? "Sua assinatura expirou. Renove para continuar usando."
                  : status.days_remaining <= 3
                    ? `Sua assinatura expira em ${status.days_remaining} dia(s). Renove agora!`
                    : `Sua assinatura expira em ${status.days_remaining} dias.`
                }
              </p>
            </div>
          )}

          {/* Renewal Button */}
          {(status?.is_expired || (status?.days_remaining !== null && status?.days_remaining <= 7)) && (
            <Button 
              onClick={() => navigate("/select-plan")}
              className="btn-primary w-full mt-4"
            >
              <RefreshCw size={16} className="mr-2" />
              Renovar assinatura
            </Button>
          )}
        </Card>

        {/* Latest Request Status */}
        {request && (
          <Card className="p-6 mb-6">
            <h3 className="font-mono text-xs uppercase tracking-widest mb-4">Última Solicitação</h3>
            
            <div className={`flex items-center gap-3 p-4 ${statusConfig.bg} mb-4`}>
              {statusConfig.icon}
              <div>
                <p className={`font-medium ${statusConfig.color}`}>{statusConfig.label}</p>
                <p className="text-sm text-muted-foreground">{statusConfig.description}</p>
              </div>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plano solicitado:</span>
                <span className="font-medium">{getPlanLabel(request.plan)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Período:</span>
                <span className="font-medium">
                  {request.period === "monthly" ? "Mensal" : "Semestral"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor:</span>
                <span className="font-medium">${request.amount?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Data da solicitação:</span>
                <span className="font-medium">{formatDate(request.created_at)}</span>
              </div>
              {request.proof_submitted_at && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Comprovante enviado:</span>
                  <span className="font-medium">{formatDate(request.proof_submitted_at)}</span>
                </div>
              )}
              {request.rejection_reason && (
                <div className="pt-3 border-t">
                  <p className="text-muted-foreground text-xs mb-1">Motivo:</p>
                  <p className="text-red-600">{request.rejection_reason}</p>
                </div>
              )}
            </div>

            {/* Actions based on status */}
            {request.status === "awaiting_proof" && (
              <Button 
                onClick={() => navigate("/payment", { state: { plan: { id: request.plan }, period: request.period } })}
                className="btn-primary w-full mt-4"
              >
                Enviar comprovante
                <ArrowRight size={16} className="ml-2" />
              </Button>
            )}

            {request.status === "correction_needed" && (
              <Button 
                onClick={() => navigate("/payment", { state: { plan: { id: request.plan }, period: request.period } })}
                className="btn-primary w-full mt-4"
              >
                Enviar novo comprovante
                <ArrowRight size={16} className="ml-2" />
              </Button>
            )}
          </Card>
        )}

        {/* No subscription yet */}
        {!request && !status?.subscription_tier && (
          <Card className="p-6 text-center">
            <AlertCircle size={48} className="mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-serif text-xl mb-2">Você ainda não tem uma assinatura</h3>
            <p className="text-muted-foreground mb-4">
              Escolha um plano para começar a usar todos os recursos da AESTHA.
            </p>
            <Button 
              onClick={() => navigate("/select-plan")}
              className="btn-primary"
            >
              Escolher plano
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Card>
        )}

        {/* Support */}
        {paymentMethods?.support && (
          <Card className="p-6">
            <h3 className="font-mono text-xs uppercase tracking-widest mb-4">Precisa de ajuda?</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Entre em contato com nosso atendimento via WhatsApp ou mensagem de texto.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a 
                href={`${paymentMethods.support.whatsapp_link}?text=${encodeURIComponent(paymentMethods.support.message)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-none hover:bg-green-700 transition-colors"
              >
                <MessageCircle size={18} />
                WhatsApp
              </a>
              <a 
                href={`sms:${paymentMethods.support.whatsapp}`}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 border text-foreground rounded-none hover:bg-secondary transition-colors"
              >
                <Phone size={18} />
                SMS
              </a>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
